import {
  Build as BuildIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface AssetData {
  id: string;
  tag_id: string;
  serial_number: string | null;
  type: string | null;
  status: 'active' | 'maintenance' | 'retired';
  created_at: string;
  customer: { name: string } | null;
}

interface ManualData {
  version: number;
  storage_path: string;
  mime_type: string | null;
  uploaded_at: string;
}

interface HistoryData {
  id: string;
  event_type: string;
  description: string | null;
  event_at: string;
}

const statusColors: Record<string, 'success' | 'warning' | 'error'> = {
  active: 'success',
  maintenance: 'warning',
  retired: 'error',
};

const eventTypeIcons: Record<string, typeof BuildIcon> = {
  service: BuildIcon,
  repair: BuildIcon,
  inspection: InfoIcon,
  manual_update: DescriptionIcon,
};

export function PublicAssetView() {
  const { tagId } = useParams<{ tagId: string }>();
  const [asset, setAsset] = useState<AssetData | null>(null);
  const [manual, setManual] = useState<ManualData | null>(null);
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssetData = useCallback(async () => {
    if (!tagId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch asset by tag_id
      const { data: assetData, error: assetError } = await supabase
        .from('assets')
        .select(
          `
          id,
          tag_id,
          serial_number,
          type,
          status,
          created_at,
          customer:customers(name)
        `
        )
        .eq('tag_id', tagId)
        .single();

      if (assetError || !assetData) {
        setError('Asset not found. The QR code may be invalid or the asset has been removed.');
        return;
      }

      setAsset(assetData as unknown as AssetData);

      // Fetch latest manual
      const { data: manualData } = await supabase
        .from('asset_manuals')
        .select('version, storage_path, mime_type, uploaded_at')
        .eq('asset_id', assetData.id)
        .order('version', { ascending: false })
        .limit(1)
        .single();

      if (manualData) {
        setManual(manualData);
        // Get signed URL for manual
        const { data: signedUrlData } = await supabase.storage
          .from('manuals')
          .createSignedUrl(manualData.storage_path, 300);
        setManualUrl(signedUrlData?.signedUrl || null);
      }

      // Fetch recent history
      const { data: historyData } = await supabase
        .from('asset_histories')
        .select('id, event_type, description, event_at')
        .eq('asset_id', assetData.id)
        .order('event_at', { ascending: false })
        .limit(5);

      setHistory((historyData as HistoryData[]) || []);
    } catch (err) {
      console.error('Error fetching asset:', err);
      setError('Unable to load asset information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tagId]);

  useEffect(() => {
    if (!tagId) {
      setError('Invalid asset identifier');
      setLoading(false);
      return;
    }

    fetchAssetData();
  }, [tagId, fetchAssetData]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading asset information...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchAssetData}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (!asset) {
    return null;
  }

  const customerName =
    asset.customer && typeof asset.customer === 'object' ? asset.customer.name : null;

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}
      >
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {asset.type || 'Asset'}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {asset.serial_number || asset.tag_id}
        </Typography>
        {customerName && (
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
            Owner: {customerName}
          </Typography>
        )}
      </Paper>

      {/* Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={500}>
              Status
            </Typography>
            <Chip
              label={asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
              color={statusColors[asset.status]}
              size="small"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Manual */}
      {manual && manualUrl && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={500}>
                  User Manual
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Version {manual.version} • {new Date(manual.uploaded_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                href={manualUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Manual
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!manual && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No manual available for this asset.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Service History */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <HistoryIcon color="action" />
            <Typography variant="subtitle1" fontWeight={500}>
              Recent Service History
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No service history recorded.
            </Typography>
          ) : (
            <List disablePadding>
              {history.map((event, index) => {
                const IconComponent = eventTypeIcons[event.event_type] || InfoIcon;
                return (
                  <ListItem key={event.id} divider={index < history.length - 1}>
                    <ListItemIcon>
                      <IconComponent fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={event.event_type.replace('_', ' ').toUpperCase()}
                      secondary={
                        <>
                          {event.description && <span>{event.description}</span>}
                          <Typography
                            component="span"
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {new Date(event.event_at).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Powered by DigniCRM
        </Typography>
      </Box>
    </Box>
  );
}
